import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { closeLiveModal, addEventToHistory, setCurrentEventIndex, setOnboardingMode } from '../features/liveSlice';
import { setHasSeenOnboarding, setCallType, setCallInProgress, setChannel, setOnboardingCallEvent, setOnboardingDefaultTourPending } from '../features/channelSlice';

export default function LiveModal() {
    const dispatch = useDispatch();
    const { videoInfo, isLiveModalOpen, clearEventsFlag, eventHistory, currentEventIndex, isOnboardingMode } = useSelector((state) => state.live);
    const onboardingCallEvent = useSelector((state) => state.channel.onboardingCallEvent);
    const onboardingDefaultTourPending = useSelector((state) => state.channel.onboardingDefaultTourPending);
    const isLightTheme = useSelector((state) => state.device.theme) === 'light';
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [isPortraitMode, setIsPortraitMode] = useState(false);
    
    // Track portrait mode for mobile and tablet
    useEffect(() => {
        const updateOrientation = () => {
            const isMobileOrTablet = window.innerWidth < 1024; // Include tablets
            const isPortrait = window.innerHeight > window.innerWidth;
            setIsPortraitMode(isMobileOrTablet && isPortrait);
        };
        
        updateOrientation();
        window.addEventListener('resize', updateOrientation);
        window.addEventListener('orientationchange', updateOrientation);
        
        return () => {
            window.removeEventListener('resize', updateOrientation);
            window.removeEventListener('orientationchange', updateOrientation);
        };
    }, []);
    
    // Use a centred modal window on desktop (preserves 16:9 video dimensions instead of
    // stretching/cropping on wide monitors). Portrait mobile/tablet has its own branch and
    // ignores this flag, so it stays unchanged.
    const isFullscreenMode = false;
    
    // Track if this is the first time opening (onboarding) - skip animation for instant display
    const hasOpenedBeforeRef = useRef(false);

    // "Tap to Begin" gate: shown ONLY when the first onboarding event arrives
    // with unmuted:true and the browser blocks autoplay (NotAllowedError).
    // Once we've handled that first attempt — by either succeeding or surfacing
    // the gate — we never show it again. Subsequent events ride on the user
    // activation from this initial tap.
    const [showTapToBegin, setShowTapToBegin] = useState(false);
    const firstOnboardingHandledRef = useRef(false);
    const pendingUnmutedVideoRef = useRef(null);
    // Mirror isOnboardingMode into a ref so the changeContent useCallback can
    // read it without being recreated (and re-binding video event handlers)
    // every time the mode flips.
    const isOnboardingModeRef = useRef(false);
    useEffect(() => {
        // Reset the TapToBegin gate at the start of every NEW onboarding cycle
        // (Reset button, brand switch, etc.). The user activation from the
        // initial page tap has expired by the time the reset chain's setTimeouts
        // fire, so play() with audio fails again — we need the gate back so
        // the user can re-tap and recover audio. Without this, the second
        // cycle's video plays muted because the ref latched true on the first.
        if (isOnboardingMode && !isOnboardingModeRef.current) {
            firstOnboardingHandledRef.current = false;
        }
        isOnboardingModeRef.current = isOnboardingMode;
    }, [isOnboardingMode]);

    console.log('🎬 LiveModal render - videoInfo:', videoInfo, 'isOpen:', isLiveModalOpen);

    // Live channel defaults (hardcoded since live channel is no longer configurable)
    const defaultVideoSrc = 'https://storage.googleapis.com/labblive/videos/boi/Server%20room%20in%20the%20clouds.mp4';
    const defaultTitle = 'Live';
    const defaultProgram = 'Idle';
    const defaultDescription = 'Awaiting new processes and actions to be sent through Pega';

    const [overlayInfo, setOverlayInfo] = useState({
        channel: defaultTitle,
        program: defaultProgram,
        description: defaultDescription,
    });

    const [contentType, setContentType] = useState('video'); // 'video' or 'image'
    const video1Ref = useRef(null);
    const video2Ref = useRef(null);
    const image1Ref = useRef(null);
    const image2Ref = useRef(null);
    const [activeMedia, setActiveMedia] = useState('media1'); // media1 or media2
    const queuedContent = useRef(null);
    
    // Track the previous videoInfo to detect new events vs modal reopening
    const prevVideoInfoRef = useRef(videoInfo);
    const isNewEventRef = useRef(false);
    const lastLoadedContentRef = useRef(null);
    const changeContentTimerRef = useRef(null);

    console.log('📊 State - contentType:', contentType, 'activeMedia:', activeMedia, 'eventHistory:', eventHistory.length, 'currentIndex:', currentEventIndex);

    const changeContent = useCallback(({
        file,
        channel,
        program,
        description,
        type = 'video',
        loop = true,
        loopAfter = '',
        immediate = true,
        unmuted = false,
        retryCount = 0
    }) => {
        console.log('changeContent called with:', { file, channel, program, type, loop, loopAfter, immediate, retryCount });

        if (!file) {
            console.warn('No file provided to changeContent');
            return;
        }

        // Prevent duplicate rapid-fire calls for the same content
        const contentKey = `${file}-${channel}-${program}`;
        if (lastLoadedContentRef.current === contentKey && retryCount === 0) {
            console.log('⏭️ Skipping duplicate changeContent call for:', contentKey);
            return;
        }
        lastLoadedContentRef.current = contentKey;

        // Update overlay info FIRST, before any async operations
        console.log('🎯 Updating overlay info:', { channel, program, description });
        setOverlayInfo({
            channel: channel || defaultTitle,
            program: program || defaultProgram,
            description: description || defaultDescription,
        });

        const isVideo = type === 'video';
        const activeElement = activeMedia === 'media1' 
            ? (isVideo ? video1Ref.current : image1Ref.current)
            : (isVideo ? video2Ref.current : image2Ref.current);
        const nextElement = activeMedia === 'media1'
            ? (isVideo ? video2Ref.current : image2Ref.current)
            : (isVideo ? video1Ref.current : image1Ref.current);

        if (!nextElement) {
            if (retryCount < 5) {
                console.warn(`⚠️ Next element not found, retrying in 100ms... (attempt ${retryCount + 1}/5)`);
                // Retry after a short delay to allow React to render the DOM
                setTimeout(() => {
                    changeContent({ file, channel, program, description, type, loop, loopAfter, immediate, retryCount: retryCount + 1 });
                }, 100);
            } else {
                console.error('❌ Failed to find video/image element after 5 retries');
            }
            return;
        }

        console.log(`Switching from ${activeMedia} to ${activeMedia === 'media1' ? 'media2' : 'media1'}`);
        console.log(`Content type: ${type}, Loop: ${loop}, LoopAfter: ${loopAfter}`);

        const playNextContent = () => {
            // Set the content type
            console.log('🎥 Setting content type to:', type);
            setContentType(type);

            if (isVideo) {
                // Handle video
                console.log(`🎬 Loading video: ${file}`);
                
                // Ensure URL is properly encoded
                let videoUrl = file;
                try {
                    // If the URL contains spaces or special characters, ensure it's properly encoded
                    const url = new URL(file);
                    videoUrl = url.href;
                    console.log(`📎 Encoded URL: ${videoUrl}`);
                } catch (e) {
                    console.warn('URL parsing failed, using original:', e);
                }
                
                // Only reset if video has been played before (has currentTime > 0 or has src)
                const needsReset = nextElement.src && nextElement.src !== '' && nextElement.currentTime > 0;
                console.log(`🔧 Video needs reset: ${needsReset}`);
                
                if (needsReset) {
                    // Reset video element before loading new content
                    nextElement.pause();
                    nextElement.currentTime = 0;
                }
                
                // Ensure crossOrigin is set before src so the browser makes a CORS request.
                // Without this, the browser may use a cached non-CORS response from S3.
                nextElement.crossOrigin = 'anonymous';
                nextElement.src = videoUrl;
                nextElement.loop = loop; // Set loop property on the element
                // Default to muted on every new event unless the payload explicitly
                // sets unmuted:true. Mobile browsers (iOS Safari especially) will
                // block autoplay on an unmuted video without prior user gesture —
                // the play() call below catches that and the retry path still runs.
                nextElement.muted = !unmuted;
                nextElement.load(); // Explicitly load the video
                
                // Add error handler for video loading
                nextElement.onerror = (e) => {
                    console.error('❌ Video load error:', e);
                    console.error('❌ Video src:', nextElement.src);
                    console.error('❌ Video error code:', nextElement.error?.code, nextElement.error?.message);
                };
                
                // Add loadeddata event to confirm video is ready
                nextElement.onloadeddata = () => {
                    console.log('✅ Video loaded successfully');
                };
                
                // Set up event handlers based on loop behavior
                if (!loop && loopAfter) {
                    // Non-looping video with loopAfter
                    console.log(`Setting up loopAfter: ${loopAfter}`);
                    nextElement.onended = () => {
                        console.log(`Video ended, playing loopAfter: ${loopAfter}`);
                        changeContent({ file: loopAfter, channel, program, description, type, loop: true, immediate: true, unmuted, retryCount: 0 });
                    };
                } else {
                    // Normal video behavior - no special end handling needed
                    nextElement.onended = null;
                }

                // Wait for video to be ready before playing
                // This is crucial for ensuring video plays on initial load
                const playVideo = () => {
                    console.log('▶️ Attempting to play video, readyState:', nextElement.readyState);

                    // Re-assert the muted state immediately before play(). playVideo can
                    // run synchronously OR be deferred to the `canplay` event on a cold
                    // load (Reset replays a fresh video at readyState<3). In that gap
                    // React re-renders (setContentType/setOverlayInfo) and re-applies the
                    // static `muted` attribute on the <video> JSX, silently flipping the
                    // DOM property back to true — so the deferred play() would otherwise
                    // start MUTED even though the payload asked for audio. Recap doesn't
                    // hit this because its video is already buffered (readyState>=3) and
                    // plays synchronously before any re-render. Forcing it here makes both
                    // paths honour unmuted:true.
                    nextElement.muted = !unmuted;
                    console.log('🔊 Asserting muted=' + nextElement.muted + ' before play (unmuted payload:', unmuted + ')');

                    nextElement.play().then(() => {
                        console.log('✅ Video started playing');
                        // Crossfade
                        if (activeElement) {
                            activeElement.pause(); // Pause the old video
                            activeElement.classList.remove('opacity-100');
                            activeElement.classList.add('opacity-0');
                        }
                        nextElement.classList.remove('opacity-0');
                        nextElement.classList.add('opacity-100');
                        setActiveMedia(activeMedia === 'media1' ? 'media2' : 'media1');
                        // First onboarding event played without needing user input —
                        // close the gate so we never show "Tap to Begin" later.
                        firstOnboardingHandledRef.current = true;
                    }).catch(error => {
                        console.error('❌ Error playing video on first attempt:', error);
                        console.error('❌ Video readyState:', nextElement.readyState);
                        console.error('❌ Video networkState:', nextElement.networkState);

                        // Autoplay-with-audio block: browsers reject play() with
                        // NotAllowedError when an unmuted video tries to start
                        // before any user gesture. For the FIRST onboarding event
                        // only, surface a "Tap to Begin" button so the user can
                        // satisfy the gesture requirement. After that tap the
                        // page has user activation and subsequent unmuted videos
                        // play directly.
                        if (
                            unmuted &&
                            error?.name === 'NotAllowedError' &&
                            isOnboardingModeRef.current &&
                            !firstOnboardingHandledRef.current
                        ) {
                            console.log('🔇 Autoplay-with-audio blocked — showing Tap to Begin gate');
                            firstOnboardingHandledRef.current = true;
                            // Capture which media slot this element belongs to
                            // so the tap handler can mirror the same crossfade /
                            // activeMedia swap the normal play path does.
                            pendingUnmutedVideoRef.current = {
                                element: nextElement,
                                activeElement,
                                nextSide: activeMedia === 'media1' ? 'media2' : 'media1',
                            };
                            setShowTapToBegin(true);
                            return;
                        }

                        // Retry once the browser has enough data to actually render
                        const retryOnReady = () => {
                            console.log('🔄 Retrying video play (readyState:', nextElement.readyState, ')');
                            nextElement.play().catch(err => {
                                console.error('❌ Retry play attempt also failed:', err);
                            });
                        };
                        if (nextElement.readyState >= 3) {
                            retryOnReady();
                        } else {
                            nextElement.addEventListener('canplay', retryOnReady, { once: true });
                        }
                    });
                };
                
                // Wait until the browser has enough data to start playback (readyState >= 3).
                // readyState 1 (HAVE_METADATA) is not enough — Chrome's power saver will
                // abort play() on muted background-tab videos if frames aren't buffered yet.
                if (nextElement.readyState >= 3) {
                    playVideo();
                } else {
                    nextElement.addEventListener('canplay', () => {
                        console.log('✅ Video canplay fired, now playing');
                        playVideo();
                    }, { once: true });
                }
            } else {
                // Handle image
                nextElement.src = file;
                // Crossfade
                if (activeElement) {
                    activeElement.classList.remove('opacity-100');
                    activeElement.classList.add('opacity-0');
                }
                nextElement.classList.remove('opacity-0');
                nextElement.classList.add('opacity-100');
                setActiveMedia(activeMedia === 'media1' ? 'media2' : 'media1');
                console.log('Image displayed');
            }
        };

        // Check if we should play immediately or queue
        if (immediate || !activeElement || (isVideo && (activeElement.paused || activeElement.ended))) {
            playNextContent();
        } else if (isVideo) {
            // Queue the content
            queuedContent.current = { file, channel, program, description, type, loop, loopAfter, immediate: true, unmuted, retryCount: 0 };
            console.log(`Content queued: ${file}`);
            activeElement.loop = false;
            activeElement.onended = () => {
                if (queuedContent.current) {
                    console.log(`Playing queued content: ${queuedContent.current.file}`);
                    changeContent(queuedContent.current);
                    queuedContent.current = null;
                }
            };
        } else {
            // For images, always play immediately
            playNextContent();
        }
    }, [activeMedia, defaultTitle, defaultProgram, defaultDescription]);

    useEffect(() => {
        if (videoInfo) {
            console.log('🔔 videoInfo changed:', JSON.stringify(videoInfo, null, 2));
            console.log('📜 Current eventHistory:', eventHistory);
            console.log('🎭 Modal open state:', isLiveModalOpen);
            console.log('🖼️ Should render:', shouldRender);

            // Re-arm the TapToBegin gate on every fresh onboarding-unmuted event.
            // Hooking this to videoInfo (not the isOnboardingMode transition) is
            // important because Reset can replay the onboarding while
            // isOnboardingMode is still true from the previous cycle — the
            // transition-based reset wouldn't fire, and play() would silently
            // fail (or play muted on Chrome) without ever surfacing the gate.
            if (videoInfo.isOnboardingEvent && videoInfo.unmuted === true) {
                firstOnboardingHandledRef.current = false;
            }

            // Normalize the videoInfo to ensure it has the right field names
            const normalizedInfo = {
                file: videoInfo.file || videoInfo.videoSrc || videoInfo.url || videoInfo.src,
                channel: videoInfo.channel || videoInfo.title || 'Live',
                program: videoInfo.program || 'Live Event',
                description: videoInfo.description || 'Live content',
                type: videoInfo.type || 'video',
                loop: videoInfo.loop !== undefined ? videoInfo.loop : true,
                loopAfter: videoInfo.loopAfter || '',
                immediate: videoInfo.immediate !== undefined ? videoInfo.immediate : true,
                // Default mute-on. Audio plays only when the payload explicitly
                // opts in with unmuted:true. Stored on the history entry so a
                // reopen-from-history replay preserves the original audio policy.
                unmuted: videoInfo.unmuted === true
            };
            
            console.log('🔄 Normalized info:', JSON.stringify(normalizedInfo, null, 2));
            
            if (!normalizedInfo.file) {
                console.error('❌ No video/image file found in payload!', videoInfo);
                return;
            }
            
            // Check if this event already exists in history
            // Compare with ALL items in history, not just the last one
            const isDuplicate = eventHistory.some(event => 
                event.file === normalizedInfo.file &&
                event.channel === normalizedInfo.channel &&
                event.program === normalizedInfo.program
            );
            
            console.log('🔍 Is duplicate?', isDuplicate);
            
            // REMOVED: The old duplicate check that would return early when modal is open
            // This was preventing Recap from working properly
            // Now we'll let the modal open effect handle replaying the content
            
            console.log('➕ Adding to event history');
            
            // Handle immediate flag logic
            if (!normalizedInfo.immediate && isLiveModalOpen) {
                // If immediate is false and modal is open, just append to history without changing view
                console.log('⏸️ immediate=false and modal open, appending to history without changing content');
                dispatch(addEventToHistory(normalizedInfo));
                console.log('📚 New history length:', eventHistory.length + 1);
                // Don't update currentEventIndex - stay on current event
                // Mark as not a new event since we're not changing content
                isNewEventRef.current = false;
            } else {
                // Either immediate=true, or modal is closed - add to history and show the event
                console.log('▶️ immediate=true or modal closed, adding to history and playing');
                // Mark that we have a new event (not just reopening modal)
                isNewEventRef.current = true;
                
                // Only add to history if it's not already there
                if (!isDuplicate) {
                    dispatch(addEventToHistory(normalizedInfo));
                }
                
                // Always set the index - either to the new event or to the existing one
                const indexToShow = isDuplicate 
                    ? eventHistory.findIndex(event => 
                        event.file === normalizedInfo.file &&
                        event.channel === normalizedInfo.channel &&
                        event.program === normalizedInfo.program
                    )
                    : eventHistory.length; // Will be the index of the newly added item
                
                console.log('📚 Setting currentEventIndex to:', indexToShow);
                dispatch(setCurrentEventIndex(indexToShow));
                
                // Change content for new events
                // Wait a bit to ensure modal has started rendering before calling changeContent
                console.log('🎬 Scheduling changeContent for new event');
                
                // Clear any pending changeContent timer to prevent duplicate calls
                if (changeContentTimerRef.current) {
                    clearTimeout(changeContentTimerRef.current);
                }
                
                changeContentTimerRef.current = setTimeout(() => {
                    console.log('🎬 Now calling changeContent for new event');
                    changeContent(normalizedInfo);
                    // Clear the flag after content loads
                    setTimeout(() => {
                        isNewEventRef.current = false;
                    }, 100);
                }, 150); // Increased delay to ensure modal is visible and video elements are rendered
            }
            
            // Update previous videoInfo
            prevVideoInfoRef.current = videoInfo;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoInfo]); // Only depend on videoInfo to prevent infinite loops
    
    // When modal opens, ensure video plays (only for reopening, not initial open)
    useEffect(() => {
        console.log('🚪 Modal open state changed:', isLiveModalOpen, 'eventHistory:', eventHistory.length, 'currentIndex:', currentEventIndex, 'isNewEvent:', isNewEventRef.current);
        
        if (isLiveModalOpen && eventHistory.length > 0) {
            const indexToShow = currentEventIndex >= 0 ? currentEventIndex : eventHistory.length - 1;
            console.log('🎯 Modal opened, showing event at index:', indexToShow);
            
            // Only set the index if it was -1
            if (currentEventIndex === -1) {
                dispatch(setCurrentEventIndex(indexToShow));
            }
            
            // Check if this is a reopen (not a new event)
            // If isNewEventRef is false, it means user clicked live button to reopen
            if (!isNewEventRef.current) {
                console.log('🔄 Modal reopened (not new event), forcing content replay');
                const eventToPlay = eventHistory[indexToShow];
                console.log('▶️ Replaying content from history:', eventToPlay);
                
                // FORCE changeContent to run by temporarily clearing the lastLoaded ref
                const previousLastLoaded = lastLoadedContentRef.current;
                lastLoadedContentRef.current = null;
                
                // Call changeContent to reload and play the video
                setTimeout(() => {
                    changeContent({ ...eventToPlay, immediate: true });
                    // Restore the lastLoaded ref after a delay
                    setTimeout(() => {
                        lastLoadedContentRef.current = previousLastLoaded;
                    }, 200);
                }, 50);
            } else {
                console.log('✓ New event is being processed, skipping reopen logic');
                // For new events, just ensure video plays if paused
                setTimeout(() => {
                    // Check both video elements and play whichever one is visible and has content
                    const video1 = video1Ref.current;
                    const video2 = video2Ref.current;
                    
                    console.log('🔍 Checking videos - video1:', {
                        exists: !!video1,
                        hasOpacity: video1?.classList.contains('opacity-100'),
                        paused: video1?.paused,
                        src: video1?.src
                    });
                    console.log('🔍 Checking videos - video2:', {
                        exists: !!video2,
                        hasOpacity: video2?.classList.contains('opacity-100'),
                        paused: video2?.paused,
                        src: video2?.src
                    });
                    
                    // Find the visible video (one with opacity-100 class)
                    let videoToPlay = null;
                    if (video1 && video1.classList.contains('opacity-100') && video1.src) {
                        videoToPlay = video1;
                    } else if (video2 && video2.classList.contains('opacity-100') && video2.src) {
                        videoToPlay = video2;
                    }
                    
                    if (videoToPlay && contentType === 'video') {
                        if (videoToPlay.paused) {
                            console.log('▶️ Playing visible video');
                            videoToPlay.play().catch(error => {
                                console.error('❌ Error playing video:', error);
                            });
                        } else {
                            console.log('✓ Video is already playing');
                        }
                    } else {
                        console.log('⚠️ No visible video found to play');
                    }
                }, 150);
            }
        } else if (isLiveModalOpen && eventHistory.length === 0) {
            // If modal opens but no history, try playing whichever video is visible
            console.log('📺 Modal opened with no history, attempting to play visible video');
            setTimeout(() => {
                const video1 = video1Ref.current;
                const video2 = video2Ref.current;
                
                // Find the visible video (one with opacity-100 class)
                let videoToPlay = null;
                if (video1 && video1.classList.contains('opacity-100') && video1.src) {
                    videoToPlay = video1;
                } else if (video2 && video2.classList.contains('opacity-100') && video2.src) {
                    videoToPlay = video2;
                }
                
                if (videoToPlay && contentType === 'video') {
                    console.log('▶️ Attempting to play visible video, paused:', videoToPlay.paused, 'readyState:', videoToPlay.readyState, 'src:', videoToPlay.src);
                    if (videoToPlay.paused) {
                        videoToPlay.play().catch(error => {
                            console.error('❌ Error playing video:', error);
                        });
                    }
                } else {
                    console.log('⚠️ No visible video element found');
                }
            }, 150);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLiveModalOpen]);
    
    // Navigation handlers
    const navigateToPrevious = () => {
        if (currentEventIndex > 0) {
            const prevIndex = currentEventIndex - 1;
            dispatch(setCurrentEventIndex(prevIndex));
            changeContent(eventHistory[prevIndex]);
        }
    };
    
    const navigateToNext = () => {
        if (currentEventIndex < eventHistory.length - 1) {
            const nextIndex = currentEventIndex + 1;
            dispatch(setCurrentEventIndex(nextIndex));
            changeContent(eventHistory[nextIndex]);
        }
    };
    
    const canNavigatePrev = currentEventIndex > 0;
    const canNavigateNext = currentEventIndex < eventHistory.length - 1;

    // Clear event history when reset is triggered
    useEffect(() => {
        console.log('🗑️ Clear events flag changed, clearing event history');
        // Note: clearLiveEvents in Redux already handles clearing state and localStorage
        
        // Clear the duplicate prevention ref to allow onboarding video to play after reset
        console.log('🧹 Clearing lastLoadedContentRef to allow content replay after reset');
        lastLoadedContentRef.current = null;
        
        // Clear all video/image sources to prevent showing old content
        if (video1Ref.current) {
            video1Ref.current.src = '';
            video1Ref.current.pause();
        }
        if (video2Ref.current) {
            video2Ref.current.src = '';
            video2Ref.current.pause();
        }
        if (image1Ref.current) {
            image1Ref.current.src = '';
        }
        if (image2Ref.current) {
            image2Ref.current.src = '';
        }
        
        setActiveMedia('media1');
        setContentType('video');
        setOverlayInfo({
            channel: defaultTitle,
            program: defaultProgram,
            description: defaultDescription,
        });
    }, [clearEventsFlag, defaultTitle, defaultProgram, defaultDescription]);

    useEffect(() => {
        // Set initial video
        if (video1Ref.current && !videoInfo) {
            video1Ref.current.src = defaultVideoSrc;
            video1Ref.current.loop = true;
            video1Ref.current.play().catch(error => console.error('Error playing default video:', error));
        }
    }, [defaultVideoSrc, videoInfo]);

    // Handle modal visibility and fade animations
    useEffect(() => {
        if (isLiveModalOpen) {
            // Modal opening: start render
            setShouldRender(true);
            
            // Check if this is the first time opening (onboarding event)
            const isFirstOpen = !hasOpenedBeforeRef.current && isOnboardingMode;
            
            if (isFirstOpen) {
                // First open (onboarding): Show instantly without animation
                console.log('🎬 First open detected (onboarding) - showing instantly');
                setIsVisible(true);
                hasOpenedBeforeRef.current = true;
            } else {
                // Subsequent opens: Use fade-in animation
                console.log('🎬 Subsequent open - using fade-in animation');
                const fadeInTimer = setTimeout(() => {
                    setIsVisible(true);
                }, 10);
                hasOpenedBeforeRef.current = true;
                return () => clearTimeout(fadeInTimer);
            }
        } else {
            // Modal closing: fade out, then stop render
            setIsVisible(false);
            const fadeOutTimer = setTimeout(() => {
                setShouldRender(false);
            }, 300); // Match transition duration
            return () => clearTimeout(fadeOutTimer);
        }
    }, [isLiveModalOpen, isOnboardingMode]);

    const handleClose = () => {
        dispatch(closeLiveModal());
    };

    // "Tap to Begin" handler — fires inside a real user gesture, so calling
    // play() on the pending unmuted video succeeds where the autoplay attempt
    // failed. We also pre-emptively unmute every other rendered video element
    // so any in-flight queued/loopAfter playback inherits the activation.
    const handleTapToBegin = () => {
        const pending = pendingUnmutedVideoRef.current;
        pendingUnmutedVideoRef.current = null;
        setShowTapToBegin(false);
        if (!pending) return;
        const { element, activeElement, nextSide } = pending;
        element.muted = false;
        element.play().then(() => {
            // Mirror the crossfade-and-swap from the normal play path so the
            // newly-playing video becomes visible and activeMedia stays in sync
            // with the actually-on-screen element.
            if (activeElement) {
                activeElement.pause();
                activeElement.classList.remove('opacity-100');
                activeElement.classList.add('opacity-0');
            }
            element.classList.remove('opacity-0');
            element.classList.add('opacity-100');
            setActiveMedia(nextSide);
        }).catch((err) => {
            console.error('❌ Tap-to-begin play() failed:', err);
        });
    };
    
    const handleOnboardingComplete = () => {
        console.log('🎓 Onboarding completed by user');
        dispatch(setHasSeenOnboarding(true));
        dispatch(setOnboardingMode(false));
        handleClose();

        if (onboardingCallEvent) {
            console.log('📞 Onboarding: starting outbound call after media events');
            const requestedType = onboardingCallEvent.callType || 'voice';
            dispatch(setCallType(requestedType));
            dispatch(setCallInProgress(true));
            dispatch(setChannel('call'));
            dispatch(setOnboardingCallEvent(null));
        } else if (onboardingDefaultTourPending) {
            console.log('🧭 Onboarding: starting default guided tour after media events');
            // Small delay so the modal close transition finishes before Joyride measures targets.
            // NOTE: the pending flag is intentionally NOT cleared here — GuidedTour clears it
            // when the default tour ends, so any waiting nudge stays gated until then.
            setTimeout(() => {
                window.startGuidedTour?.();
            }, 500);
        }
    };

    if (!shouldRender) return null;

    // Guard: never auto-display the "Nothing to see here" empty state. The
    // Recap button is already gated on eventHistory.length > 0, so the only
    // way we'd reach this point with empty history is an automatic open
    // (reset, websocket, etc.) that doesn't have content lined up.
    if (eventHistory.length === 0 && !videoInfo) return null;

    return (
        <div 
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-all duration-300 ease-out ${isFullscreenMode ? 'p-0' : 'p-4 max-md:p-0'}`}
            style={{ 
                zIndex: 10000,
                opacity: isVisible ? 1 : 0,
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                overflow: 'auto',
                // Always vertically centre the modal so the video sits in the middle of the viewport
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isPortraitMode ? '16px' : '0'
            }}
            onClick={handleClose}
        >
            {/* Video Container */}
            <div 
                className={`w-full transition-all duration-300 flex flex-col ${isFullscreenMode && !isPortraitMode ? 'max-w-none h-full rounded-none' : 'max-w-5xl rounded-lg'}`}
                style={{
                    transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                    opacity: isVisible ? 1 : 0,
                    // In portrait mode, don't fill full height - let content determine size
                    maxHeight: isPortraitMode ? '90vh' : '100%',
                    height: isPortraitMode ? 'auto' : (isFullscreenMode ? '100%' : 'auto'),
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Video, Description, and Navigation - All in one bordered container */}
                <div className={`bg-[#1C2837] shadow-2xl overflow-hidden flex flex-col ${isFullscreenMode && !isPortraitMode ? 'rounded-none flex-1' : 'rounded-lg'}`}>
                    {/* Show "No Events" message if event history is empty */}
                    {eventHistory.length === 0 ? (
                        <>
                            {/* No Events Display with enhanced design */}
                            <div className="relative aspect-video bg-gradient-to-br from-[#0A0F1A] via-[#1C2837] to-[#0A0F1A] overflow-hidden flex items-center justify-center">
                                {/* Animated Background Pattern */}
                                <div className="absolute inset-0 opacity-5">
                                    <div className="absolute inset-0" style={{
                                        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(68, 194, 253, 0.3) 1px, transparent 0)',
                                        backgroundSize: '40px 40px'
                                    }}></div>
                                </div>
                                
                                {/* Animated Glow Orbs */}
                                <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#44C2FD]/5 rounded-full blur-3xl" style={{
                                    animation: 'pulse 8s ease-in-out infinite'
                                }}></div>
                                <div className="absolute bottom-1/3 left-1/3 w-48 h-48 bg-[#23A6D5]/5 rounded-full blur-3xl" style={{
                                    animation: 'pulse 10s ease-in-out infinite',
                                    animationDelay: '2s'
                                }}></div>
                                
                                {/* Close Button */}
                                <button
                                    onClick={handleClose}
                                    className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-[#2C3544] to-[#1a2332] hover:from-[#3a4556] hover:to-[#2C3544] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-90 z-20 border-2 border-gray-600/30 shadow-lg"
                                    aria-label="Close live modal"
                                >
                                    <span className="material-icons text-white">close</span>
                                </button>

                                {/* No Events Message with glassmorphism */}
                                <div className="text-center px-8 z-10 max-w-2xl">
                                    <div className="mb-6 relative">
                                        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-[#44C2FD]/20 to-[#23A6D5]/20 rounded-2xl flex items-center justify-center border-2 border-[#44C2FD]/30 shadow-lg shadow-[#44C2FD]/20">
                                            <span className="material-icons text-[#44C2FD]" style={{ fontSize: '48px' }}>
                                                video_library
                                            </span>
                                        </div>
                                        {/* Glow behind icon */}
                                        <div className="absolute inset-0 blur-2xl bg-[#44C2FD] opacity-10"></div>
                                    </div>
                                    <h3 className="text-white text-3xl font-semibold mb-4" style={{
                                        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                                        letterSpacing: '-0.01em'
                                    }}>
                                        Nothing to see here.
                                    </h3>
                                    <p className="text-gray-400 text-base leading-relaxed" style={{
                                        fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif'
                                    }}>
                                        Live events will automatically appear here when they are triggered through your journey. These events include videos and images to represent events that are occuring in the real world (e.g. a letter being received, a device being broken, finding a new home).
                                    </p>                                  
                                 </div>
                            </div>
                        </>
                    ) : (
                        <>
                    {/* Video/Image Display */}
                    <div 
                        className="relative bg-black overflow-hidden"
                        style={{
                            // On portrait mobile, use a smaller fixed aspect ratio; otherwise use 16:9
                            aspectRatio: isPortraitMode ? '4/3' : '16/9',
                            minHeight: isPortraitMode ? '200px' : 'auto',
                        }}
                    >
                        {/* Media Container */}
                    <div className="absolute top-0 left-0 w-full h-full">
                        {/* Videos */}
                        <video
                            ref={video1Ref}
                            className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-1000 ${
                                contentType === 'video' && activeMedia === 'media1' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                            autoPlay
                            muted
                            playsInline
                            crossOrigin="anonymous"
                        ></video>
                        <video
                            ref={video2Ref}
                            className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-1000 ${
                                contentType === 'video' && activeMedia === 'media2' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                            autoPlay
                            muted
                            playsInline
                            crossOrigin="anonymous"
                        ></video>
                        
                        {/* Images */}
                        <img
                            ref={image1Ref}
                            alt="Live content"
                            className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-1000 ${
                                contentType === 'image' && activeMedia === 'media1' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        />
                        <img
                            ref={image2Ref}
                            alt="Live content"
                            className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-1000 ${
                                contentType === 'image' && activeMedia === 'media2' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                        />
                    </div>

                    {/* Live Indicator */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 px-3 py-1.5 rounded-full z-10">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                        <span className="text-white text-sm font-bold uppercase">Live</span>
                    </div>

                    {/* Tap to Begin gate — appears only when the first onboarding
                      * event needs unmuted autoplay but the browser blocked it.
                      * Styled to match the Continue button below so the user
                      * recognises it as the primary forward action. */}
                    {showTapToBegin && (
                        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                            <button
                                onClick={handleTapToBegin}
                                className="px-10 py-4 rounded-lg font-semibold text-base transition-all duration-200 flex items-center gap-2 text-white uppercase bg-[#44C2FD] hover:bg-[#3AB0E8] shadow-lg shadow-[#44C2FD]/30"
                                style={{ letterSpacing: '0.05em' }}
                                aria-label="Tap to begin"
                            >
                                <span>TAP TO BEGIN</span>
                                <span className="material-icons-outlined">arrow_forward</span>
                            </button>
                        </div>
                    )}

                    {/* Call to Action Button - Only show on final event */}
                    {currentEventIndex === eventHistory.length - 1 && eventHistory.length > 0 && (
                        <button
                            onClick={isOnboardingMode ? handleOnboardingComplete : handleClose}
                            className="absolute px-10 py-4 rounded-lg font-semibold text-base transition-all duration-200 flex items-center gap-2 text-white z-10 uppercase bg-[#44C2FD] hover:bg-[#3AB0E8]"
                            style={{ 
                                bottom: 'calc(1rem - 5px)', 
                                right: 'calc(1rem - 10px)',
                                letterSpacing: '0.05em'
                            }}
                            aria-label={isOnboardingMode ? "Complete onboarding" : "Close and take action"}
                        >
                            <span>{isOnboardingMode ? "CONTINUE" : "CONTINUE"}</span>
                            <span className="material-icons-outlined">arrow_forward</span>
                        </button>
                    )}
                    
                    {/* Navigation Arrows - Only show if there are multiple events */}
                    {eventHistory.length > 1 && (
                        <>
                            {/* Previous Button */}
                            <button
                                onClick={navigateToPrevious}
                                disabled={!canNavigatePrev}
                                className={`absolute left-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20 ${
                                    canNavigatePrev
                                        ? 'bg-black/60 hover:bg-black/80 text-white cursor-pointer'
                                        : 'bg-black/30 text-gray-600 cursor-not-allowed'
                                }`}
                                aria-label="Previous event"
                            >
                                <span className="material-icons text-3xl">chevron_left</span>
                            </button>
                            
                            {/* Next Button — blue while there's still an event ahead
                              * (i.e. Continue isn't shown yet), so users can see the
                              * primary forward action at a glance. */}
                            <button
                                onClick={navigateToNext}
                                disabled={!canNavigateNext}
                                className={`absolute right-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all z-20 ${
                                    canNavigateNext
                                        ? 'bg-[#44C2FD] hover:bg-[#3AB0E8] text-white cursor-pointer shadow-lg shadow-[#44C2FD]/30'
                                        : 'bg-black/30 text-gray-600 cursor-not-allowed'
                                }`}
                                aria-label="Next event"
                            >
                                <span className="material-icons text-3xl">chevron_right</span>
                            </button>
                            
                            {/* Event Counter */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1.5 rounded-full z-10">
                                <span className="text-white text-sm font-medium">
                                    {currentEventIndex + 1} / {eventHistory.length}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Channel & Program Information */}
                <div className="bg-[#141D2B]/90 backdrop-blur-sm p-6 cara-chrome" style={{ background: isLightTheme ? '#f3f5f7' : undefined }}>
                    <div className="flex items-start gap-4">
                        {/* Channel & Program Details */}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-lg mb-0.5">
                                {overlayInfo.channel}
                            </h3>
                            
                            <p className="text-gray-300 text-base mb-0.5">
                                {overlayInfo.program}
                            </p>
                            
                            <p className="text-gray-400 text-sm">
                                {overlayInfo.description}
                            </p>
                        </div>
                    </div>
                </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
